import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Card,
  CardContent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Edit, AddCircle } from '@mui/icons-material';
import { QuestionType, QuestionFormData } from '../../types/survey';

const StyledCard = styled(Card)(({ theme }) => ({
  border: '1px solid #EAECEF',
  borderRadius: theme.spacing(2),
  boxShadow: 'none',
  marginBottom: theme.spacing(3)
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: theme.spacing(1.5),
  backgroundColor: 'rgba(74, 144, 226, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

interface ManualQuestionFormProps {
  onAddQuestion: (questionData: QuestionFormData) => void;
}

const ManualQuestionForm = ({ onAddQuestion }: ManualQuestionFormProps) => {
  const [question, setQuestion] = useState('');
  const [answerType, setAnswerType] = useState<QuestionType>('rating');
  const [isMandatory, setIsMandatory] = useState(false);

  const handleAddQuestion = () => {
    if (question.trim()) {
      onAddQuestion({
        question: question.trim(),
        answerType,
        isMandatory
      });
      
      // Reset form
      setQuestion('');
      setAnswerType('rating');
      setIsMandatory(false);
    }
  };

  return (
    <StyledCard>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconContainer>
            <Edit sx={{ fontSize: 24, color: '#4A90E2' }} />
          </IconContainer>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: '1.25rem',
              color: 'text.primary'
            }}
          >
            Add Question Manually
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Question Input */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                color: 'text.primary',
                mb: 1
              }}
            >
              Question
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g., How would you rate our product?"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>

          {/* Answer Type and Mandatory */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              alignItems: 'end'
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  color: 'text.primary',
                  mb: 1
                }}
              >
                Answer Type
              </Typography>
              <Select
                fullWidth
                value={answerType}
                onChange={(e) => setAnswerType(e.target.value as QuestionType)}
                sx={{
                  backgroundColor: 'white',
                  fontSize: '0.875rem'
                }}
              >
                <MenuItem value="rating">Rating (1-5)</MenuItem>
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="yesno">Yes/No</MenuItem>
              </Select>
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isMandatory}
                    onChange={(e) => setIsMandatory(e.target.checked)}
                    sx={{
                      color: '#D1D5DB',
                      '&.Mui-checked': {
                        color: '#4A90E2'
                      }
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      color: 'text.primary'
                    }}
                  >
                    Mandatory Question
                  </Typography>
                }
              />
            </Box>
          </Box>

          {/* Add Question Button */}
          <Button
            variant="contained"
            startIcon={<AddCircle />}
            onClick={handleAddQuestion}
            disabled={!question.trim()}
            sx={{
              width: 'fit-content',
              height: 40,
              backgroundColor: '#4A90E2',
              fontWeight: 700,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#3A7BC8'
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF'
              }
            }}
          >
            Add Question
          </Button>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default ManualQuestionForm;

